import React, {Dispatch, FC, SetStateAction, useEffect, useMemo, useRef, useState} from 'react';
import {Button, Form, Input, Row, Col, Select, Checkbox, Alert} from "antd";
import {useAppDispatch, useAppSelector} from "../../../store/store";
import {clearChatMessages, getChatMessages, IChat, sendMessage} from "../../../store/reducers/chatReducer/chatReducer";
import {CustomCard} from "../../UI/AntCustom/CustomCard";
import {ChatMyMessage} from "./ChatMyMessage";
import {ChatStrangerMessage} from "./ChatStrangerMessage";
import {useIsMobile} from "../../../hooks/useWindowSize";
import {mediaScreenSizes} from "../../../utils/constans/styles/screenSizes";
import {Loader} from "../../UI/Loader/Loader";
import {CustomModal} from "../../UI/AntCustom/CustomModal";
import {defineMessages, useIntl} from "react-intl";
import styled, {createGlobalStyle} from "styled-components";
import {readNotifications} from "../../../store/reducers/notificationsReducer/notificationsReducer";
import {fetchMessageTemplates, IMessageTemplateOption} from "../../../utils/helpers/selectDataFetchers/fetchMessageTemplates";
import {Image} from "antd";
import companyAvatarDefaultImage from "../../../media/images/default_avatar.png";
import ChevronDownIcon from "../../../media/icons/chevron-down.svg?react";
import type {BaseSelectRef} from "rc-select";

const MessagesWrapper = styled.div`
  display: flex;
  flex-direction: column-reverse;
  row-gap: 16px;
`;

const LoaderWrapper = styled.div`
  max-width: inherit;
  overflow: hidden;
`;

const CustomChatModal = styled(CustomModal)`
  @media(max-width: ${mediaScreenSizes.mobile}) {
    max-width: none !important;
    width: 100vw !important;
    height: 100vh;
    height: var(--app-height);
    margin: 0;

    .ant-modal-content {
      border-radius: 0;
      height: 100vh;
      height: var(--app-height);
      display: flex;
      flex-direction: column;
    }
    
    .ant-modal-body {
      padding: 10px;
      flex: 1;
    }
  }
`;

const AvatarWrapper = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 200%;
  overflow: hidden;
  flex-shrink: 0;
  margin-top: 40px;
`;

const AvatarImage = styled(Image)`
  width: 32px;
  height: 32px;
  border-radius: 200%;
  object-fit: cover;
  
  img {
    width: 32px;
    height: 32px;
    border-radius: 200%;
    object-fit: cover;
  }
`;

const StyledChevronDownIcon = styled(ChevronDownIcon)`
  width: 12px;
  height: 12px;
  pointer-events: none;
`;

const SelectScrollbarStyles = createGlobalStyle`
  /* Скрываем все скроллбары везде */
  .ant-select-dropdown {
    overflow: hidden !important;
    
    &, & * {
      &::-webkit-scrollbar {
        display: none !important;
        width: 0 !important;
        height: 0 !important;
        background: transparent !important;
      }
      scrollbar-width: none !important;
      -ms-overflow-style: none !important;
    }
    
    /* Скрываем на menu */
    .ant-select-dropdown-menu {
      overflow: hidden !important;
    }
    
    /* Скрываем на virtual-list */
    .rc-virtual-list {
      overflow: hidden !important;
    }
    
    /* Скрываем скроллбар на внутренних элементах */
    .rc-virtual-list-scrollbar {
      display: none !important;
    }
    
    /* Кастомный скроллбар только на rc-virtual-list-holder */
    .rc-virtual-list-holder {
      overflow-y: auto !important;
      overflow-x: hidden !important;
      
      /* Для Firefox - кастомный цвет */
      scrollbar-width: thin !important;
      scrollbar-color: #CDD1DB transparent !important;
      
      /* Для WebKit - кастомный скроллбар */
      &::-webkit-scrollbar {
        width: 6px !important;
        display: block !important;
        background: transparent !important;
      }
      
      &::-webkit-scrollbar-track {
        background: transparent !important;
      }
      
      &::-webkit-scrollbar-thumb {
        background: #CDD1DB !important;
        border-radius: 8px !important;
      }
      
      &::-webkit-scrollbar-thumb:hover {
        background: #CDD1DB !important;
      }
      
      /* Скрываем все дочерние скроллбары */
      & * {
        &::-webkit-scrollbar {
          display: none !important;
          width: 0 !important;
          height: 0 !important;
        }
        scrollbar-width: none !important;
        -ms-overflow-style: none !important;
      }
    }
  }
`;

const SelectDropdownWrapper = styled.div`
  position: relative;
  .ant-select-dropdown {
    border-radius: 8px !important;
    margin-top: 8px !important;
    
    .ant-select-item {
      padding: 12px 16px !important;
      color: #181C25 !important;
      
      &:hover, &.ant-select-item-option-active {
        color: #181C25 !important;
      }
    }
    
    .rc-virtual-list {
      max-height: 108px !important;
      border-radius: 8px !important;
      overflow: hidden !important;
    }
    
    .rc-virtual-list-holder {
      max-height: 108px !important;
      overflow-y: auto !important;
      overflow-x: hidden !important;
    }
  }
`;

const StyledSelect = styled(Select)`
  .ant-select-selector {
    border-radius: 5px !important;
    border-color: #D9DAD9 !important;
  }
  
  .ant-select-arrow {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 0;
    top: 50%;
    transform: translateY(-50%);
    right: 8px;
  }
`;

const StyledTextArea = styled(Input.TextArea)`
  border-radius: 5px !important;
  border-color: #D9DAD9 !important;
  
  &:focus, &:hover {
    border-color: #D9DAD9 !important;
  }
`;

const SendButton = styled(Button)`
  border-radius: 5px !important;
  background-color: #1890FF !important;
  border-color: #1890FF !important;
  
  &:hover, &:focus {
    background-color: #1890FF !important;
    border-color: #1890FF !important;
  }
  
  &:disabled, &.ant-btn-disabled {
    background-color: #D9D9D9 !important;
    border-color: #D9D9D9 !important;
    color: rgba(0, 0, 0, 0.25) !important;
    cursor: not-allowed !important;
    
    &:hover, &:focus {
      background-color: #D9D9D9 !important;
      border-color: #D9D9D9 !important;
    }
  }
`;

const TemplateTriggerButton = styled(Button)`
  border-radius: 5px !important;
  background-color: #FFFFFF !important;
  border-color: #1890FF !important;
  color: #1890FF !important;

  &:hover, &:focus {
    background-color: #FFFFFF !important;
    border-color: #1890FF !important;
    color: #1890FF !important;
    opacity: 0.85;
  }
`;

const ChatModalMessages = defineMessages({
  yourMessage: {
    id: "chatModal.yourMessage",
    defaultMessage: "Your message",
  },
  selectTemplate: {
    id: "chatModal.selectTemplate",
    defaultMessage: "Select template",
  },
  sendBcc: {
    id: "chatModal.sendBcc",
    defaultMessage: "Send a copy to email",
  },
  send: {
    id: "chatModal.send",
    defaultMessage: "Send",
  },
  openTemplateSelect: {
    id: "chatModal.openTemplateSelect",
    defaultMessage: "Template",
  },
  templateOverwriteTitle: {
    id: "chatModal.templateOverwriteTitle",
    defaultMessage: "Change the message text?",
  },
  templateOverwriteDescription: {
    id: "chatModal.templateOverwriteDescription",
    defaultMessage: "The existing message text will be changed to the template's text. Proceed?",
  },
  templateOverwriteConfirm: {
    id: "chatModal.templateOverwriteConfirm",
    defaultMessage: "Change",
  },
  templateOverwriteCancel: {
    id: "chatModal.templateOverwriteCancel",
    defaultMessage: "Cancel",
  },
  chatUnavailable: {
    id: "chatModal.chatUnavailable",
    defaultMessage: "Chat service is temporarily unavailable. Please try again later.",
  },
})

interface IChatModal {
  chat: IChat;
  isVisible: boolean;
  setIsVisible: Dispatch<SetStateAction<boolean>>;
}

export const ChatModal: FC<IChatModal> = ({
                                            chat,
                                            isVisible,
                                            setIsVisible,
                                          }) => {
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const isMobile = useIsMobile();
  const {
    chatMessages,
    isChatMessagesLoading,
    isMessageSendLoading,
    hasLoadError
  } = useAppSelector(state => state.chat);
  const myCompanyId = useAppSelector(state => state.company.oneItemObject.item.id)
  const myCompany = useAppSelector(state => state.company.oneItemObject.item);
  const myUserId = useAppSelector(state => state.user.oneItemObject.item.id);
  const [newMessageText, setNewMessageText] = useState<string>('');
  const [sendBcc, setSendBcc] = useState<boolean>(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | undefined>(undefined);
  const [selectedTemplate, setSelectedTemplate] = useState<IMessageTemplateOption | null>(null);
  const [templatesOptions, setTemplatesOptions] = useState<IMessageTemplateOption[]>([]);
  const [isTemplatesLoading, setIsTemplatesLoading] = useState<boolean>(false);
  const [isTemplateDropdownOpen, setIsTemplateDropdownOpen] = useState<boolean>(false);
  const [isTemplateOverwriteModalVisible, setIsTemplateOverwriteModalVisible] = useState<boolean>(false);
  const [pendingTemplateOption, setPendingTemplateOption] = useState<IMessageTemplateOption | null>(null);
  const templateSelectRef = useRef<BaseSelectRef | null>(null);
  const chatName = chat.fromUser.company.companyId !== myCompanyId ? chat.fromUser.company.companyName : chat.toUser.company.companyName;
  const allUnreadNotifications = useAppSelector(state => state.notifications.unreadOnlyNotifications).filter(n => n.entityId === chat.chatId);
  
  const myCompanyAvatarUrl = myCompany.avatar?.url || companyAvatarDefaultImage;
  
  // Показываем селект только для empty leg или для запросов от брокера (когда есть orderId)
  const shouldShowTemplateSelect = chat.emptyLegId !== undefined || chat.orderId > 0;

  useEffect(() => {
    dispatch(getChatMessages({
      chatId: chat.chatId,
    }))

    return () => {
      dispatch(clearChatMessages());
    }
  }, []);

  useEffect(() => {
    if (isVisible && shouldShowTemplateSelect && templatesOptions.length === 0) {
      setIsTemplatesLoading(true);
      fetchMessageTemplates().then(templates => {
        setTemplatesOptions(templates);
        setIsTemplatesLoading(false);
      }).catch(() => {
        setIsTemplatesLoading(false);
      });
    }
  }, [isVisible, shouldShowTemplateSelect]);

  useEffect(() => {
    if (allUnreadNotifications.length > 0)
      dispatch(readNotifications({notificationIds: allUnreadNotifications.map(n => n.id)}))
  }, [allUnreadNotifications.length])

  const isSendButtonDisabled = useMemo(() => {
    return newMessageText.replace(new RegExp(" ", "g"), '') === "" || hasLoadError;
  }, [newMessageText, hasLoadError]);

  const recipientUserId = useMemo(() => {
    if (chat.fromUser.company.companyId === myCompanyId) {
      return chat.toUser.userId;
    }

    if (chat.toUser.company.companyId === myCompanyId) {
      return chat.fromUser.userId;
    }

    return chat.toUser.userId;
  }, [chat.fromUser.company.companyId, chat.fromUser.userId, chat.toUser.company.companyId, chat.toUser.userId, myCompanyId]);

  const onMessageSend = () => {
    dispatch(sendMessage({
      chatId: chat.chatId,
      orderId: chat.orderId,
      toUserId: recipientUserId,
      messageText: newMessageText.trim(),
      sendEmailCopy: sendBcc,
    }));

    setNewMessageText('');
    setSelectedTemplateId(undefined);
    setSelectedTemplate(null);
    setSendBcc(false);
  }

  const clearTemplateSelection = () => {
    setSelectedTemplateId(undefined);
    setSelectedTemplate(null);
    setNewMessageText('');
    setPendingTemplateOption(null);
    setIsTemplateDropdownOpen(false);
  };

  const applyTemplateOption = (template: IMessageTemplateOption) => {
    const numericValue = typeof template.value === 'number' ? template.value : Number(template.value);
    setSelectedTemplateId(Number.isNaN(numericValue) ? undefined : numericValue);
    setSelectedTemplate(template);
    if (template.text) {
      setNewMessageText(template.text);
    } else {
      setNewMessageText('');
    }
    setIsTemplateDropdownOpen(false);
  };

  const handleTemplateChange = (value: number | unknown) => {
    const templateValue = value as number | undefined;
    if (templateValue === undefined) {
      clearTemplateSelection();
      return;
    }

    const foundTemplate = templatesOptions.find(t => t.value === templateValue);
    if (!foundTemplate) {
      clearTemplateSelection();
      return;
    }

    const currentText = newMessageText.trim();
    const templateText = (foundTemplate.text || '').trim();
    const hasExistingContent = currentText.length > 0 && currentText !== templateText;

    if (hasExistingContent) {
      setPendingTemplateOption(foundTemplate);
      setIsTemplateOverwriteModalVisible(true);
      setIsTemplateDropdownOpen(false);
      return;
    }

    applyTemplateOption(foundTemplate);
  };

  const handleTemplateOverwriteConfirm = () => {
    if (pendingTemplateOption) {
      applyTemplateOption(pendingTemplateOption);
    }
    setPendingTemplateOption(null);
    setIsTemplateOverwriteModalVisible(false);
  };

  const handleTemplateOverwriteCancel = () => {
    setPendingTemplateOption(null);
    setIsTemplateOverwriteModalVisible(false);
  };

  const triggerTemplateSelect = () => {
    if (!shouldShowTemplateSelect) {
      return;
    }

    if (isMobile) {
      setIsTemplateDropdownOpen(true);
      templateSelectRef.current?.focus();
    } else {
      templateSelectRef.current?.focus();
    }
  };

  const selectContainerStyles = isMobile ? {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    opacity: 0,
    pointerEvents: 'none' as const,
    width: '100%',
    minWidth: 240,
    visibility: 'hidden' as const,
    marginBottom: 0,
  } : {
    marginBottom: 8,
  };

  return (
    <>
      {shouldShowTemplateSelect && <SelectScrollbarStyles />}
      <CustomChatModal
        open={isVisible}
        title={chatName}
        centered
        footer={false}
      onCancel={() => {
        setIsVisible(false);
        setNewMessageText('');
        setSelectedTemplateId(undefined);
        setSelectedTemplate(null);
        setIsTemplateDropdownOpen(false);
        setIsTemplateOverwriteModalVisible(false);
        setPendingTemplateOption(null);
        setSendBcc(false);
      }}
      afterClose={() => {
        dispatch(clearChatMessages());
        setNewMessageText('');
        setSelectedTemplateId(undefined);
        setSelectedTemplate(null);
        setIsTemplateDropdownOpen(false);
        setIsTemplateOverwriteModalVisible(false);
        setPendingTemplateOption(null);
        setSendBcc(false);
      }}
      bodyStyle={{
        paddingTop: 0,
        ...(isMobile ? {
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        } : {})
      }}
    >
      {hasLoadError && (
        <Alert
          message={intl.formatMessage(ChatModalMessages.chatUnavailable)}
          type="error"
          showIcon
          style={{ marginTop: isMobile ? 10 : 24, marginBottom: 16 }}
        />
      )}
      <CustomCard width={'max'}
                  noBottomBorderRadius={true}
                  borderless={true}
                  boxShadow={false}
                  bodyStyle={{
                    padding: 0,
                    boxShadow: "none",
                  }}
                  style={{
                    ...(isMobile ? {
                      flex: 1,
                      minHeight: 0,
                    } : {
                      height: hasLoadError ? 'calc(400px - 56px)' : '400px',
                    }),
                    display: "flex",
                    flexDirection: "column-reverse",
                    overflowX: "hidden",
                    padding: "16px 0",
                    overflow: "auto",
                    borderRadius: 0,
                  }}>
        {isChatMessagesLoading
            ? <LoaderWrapper><Loader/></LoaderWrapper>
            : <MessagesWrapper>
            {chatMessages.map(cm => {
              const isMyMessage = cm.fromUser.userId === myUserId;
              const isFromMyCompany = cm.fromUser.company.companyId === myCompanyId;
              // Показываем имя отправителя, если сообщение от сотрудника той же компании (но не от текущего пользователя)
              const isFromCompanyMember = isFromMyCompany && !isMyMessage;
              
              return isMyMessage
                ? <ChatMyMessage key={cm.chatMessageId} messageText={cm.messageText} sendDate={cm.sendDate}/>
                : <ChatStrangerMessage 
                    key={cm.chatMessageId} 
                    messageText={cm.messageText} 
                    sendDate={cm.sendDate}
                    fromUser={cm.fromUser}
                    showSenderName={isFromCompanyMember}
                  />;
            })}
          </MessagesWrapper>
        }
      </CustomCard>
      <Form onFinish={() => onMessageSend()}>
        <Row gutter={8} align="top">
          {!isMobile && (
            <Col flex="0 0 auto">
              <AvatarWrapper>
                <AvatarImage
                  src={myCompanyAvatarUrl}
                  alt="Avatar"
                  preview={false}
                />
              </AvatarWrapper>
            </Col>
          )}
          <Col flex={1}>
            {shouldShowTemplateSelect && (
              <SelectDropdownWrapper>
                <div style={selectContainerStyles}>
                  <StyledSelect
                    ref={templateSelectRef}
                    placeholder={intl.formatMessage(ChatModalMessages.selectTemplate)}
                    value={selectedTemplateId}
                    onChange={handleTemplateChange}
                    options={templatesOptions}
                    loading={isTemplatesLoading}
                    allowClear={true}
                    style={{ width: '100%' }}
                    showSearch={false}
                    suffixIcon={<StyledChevronDownIcon />}
                    dropdownStyle={{ maxHeight: 108, maxWidth: isMobile ? 'calc(100vw - 32px)' : undefined }}
                    listHeight={108}
                    virtual={false}
                    dropdownMatchSelectWidth={!isMobile}
                    open={isMobile ? isTemplateDropdownOpen : undefined}
                    onDropdownVisibleChange={(open) => {
                      if (isMobile) {
                        setIsTemplateDropdownOpen(open);
                      }
                    }}
                    getPopupContainer={(trigger) => isMobile ? document.body : (trigger.parentElement || document.body)}
                  />
                </div>
              </SelectDropdownWrapper>
            )}
            <StyledTextArea
              placeholder={intl.formatMessage(ChatModalMessages.yourMessage)}
              value={newMessageText}
              onChange={(e) => setNewMessageText(e.target.value)}
              autoSize={{ minRows: 4, maxRows: 8 }}
              style={{ resize: 'none' }}
              autoFocus
            />
            <div style={{ marginTop: 16 }}>
              <Checkbox
                checked={sendBcc}
                onChange={(event) => setSendBcc(event.target.checked)}
              >
                {intl.formatMessage(ChatModalMessages.sendBcc)}
              </Checkbox>
            </div>
          </Col>
        </Row>
        <Row justify={isMobile && shouldShowTemplateSelect ? "space-between" : "end"} style={{ marginTop: 12 }}>
          {isMobile && shouldShowTemplateSelect && (
            <Col>
              <TemplateTriggerButton
                type="default"
                size="large"
                onClick={triggerTemplateSelect}
              >
                {intl.formatMessage(ChatModalMessages.openTemplateSelect)}
              </TemplateTriggerButton>
            </Col>
          )}
          <Col>
            <SendButton
              type={'primary'} 
              htmlType={'submit'} 
              loading={isMessageSendLoading}
              disabled={isSendButtonDisabled}
              size="large"
            >
              {intl.formatMessage(ChatModalMessages.send)}
            </SendButton>
          </Col>
        </Row>
      </Form>

      </CustomChatModal>

      <CustomModal
        open={isTemplateOverwriteModalVisible}
        title={intl.formatMessage(ChatModalMessages.templateOverwriteTitle)}
        centered
        footer={null}
        onCancel={handleTemplateOverwriteCancel}
      >
        <div style={{ marginBottom: 24 }}>
          {intl.formatMessage(ChatModalMessages.templateOverwriteDescription)}
        </div>
        <Row justify="end" gutter={8}>
          <Col>
            <Button onClick={handleTemplateOverwriteCancel}>
              {intl.formatMessage(ChatModalMessages.templateOverwriteCancel)}
            </Button>
          </Col>
          <Col>
            <SendButton type="primary" onClick={handleTemplateOverwriteConfirm}>
              {intl.formatMessage(ChatModalMessages.templateOverwriteConfirm)}
            </SendButton>
          </Col>
        </Row>
      </CustomModal>
    </>
  );
};
